import { z } from 'zod';

import { dashboardConfigSchema } from './dashboard-config.schema';

export const directionSchema = z.enum(['ALL', 'INBOUND', 'OUTBOUND']);
export type Direction = z.infer<typeof directionSchema>;

export const timePresetSchema = z.enum(['all', 'in_hours', 'after_hours', 'custom']).default('all');
export type TimePreset = z.infer<typeof timePresetSchema>;

/**
 * What dashboard metrics count: every call, or distinct contacts (one per
 * classification per Europe/London day — a contact with 5 booked calls in a
 * day counts once, and only their latest call appears in drill-downs).
 */
export const analyticsModeSchema = z.enum(['calls', 'contacts']);
export type AnalyticsMode = z.infer<typeof analyticsModeSchema>;

export const dashboardQuerySchema = z.object({
  direction: directionSchema.default('ALL'),
  executedAtFrom: z.coerce.date().optional(),
  executedAtTo: z.coerce.date().optional(),
  timePreset: timePresetSchema,
  customTimeFrom: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  customTimeTo: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  campaignId: z.string().uuid().optional(),
  callTransport: z.enum(['PSTN', 'WEBRTC', 'WHATSAPP', 'WEB_WIDGET', 'EMAIL']).optional(),
  type: z.enum(['analytics', 'quality']).default('analytics'),
  // Optional (not defaulted) so existing internal DashboardQuery literals
  // stay valid; the service treats undefined as 'calls'.
  mode: analyticsModeSchema.optional(),
});
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

// --- Compare KPIs across configuration groups ---
//
// Each group runs in its own absolute date range, so an absolute From/To
// across groups is meaningless. Instead the window is *relative* and anchored
// per group on that group's own first/last call:
//   - first_days N: the first N days starting from the group's earliest call
//   - last_days N:  the last N days ending on the group's most recent call
//   - day_range X..Y: from day X to day Y, counting day 1 as the group's first call
export const compareWindowModeSchema = z.enum(['all', 'first_days', 'last_days', 'day_range']);
export type CompareWindowMode = z.infer<typeof compareWindowModeSchema>;

export const compareConfigurationsSchema = z
  .object({
    groups: z
      .array(
        z.object({
          id: z.string(),
          versionIds: z.array(z.string().uuid()),
        }),
      )
      .min(1),
    direction: directionSchema.default('ALL'),
    callTransport: z.enum(['PSTN', 'WEBRTC', 'WHATSAPP', 'WEB_WIDGET', 'EMAIL']).optional(),
    timePreset: timePresetSchema,
    customTimeFrom: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional(),
    customTimeTo: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional(),
    windowMode: compareWindowModeSchema.default('all'),
    firstDays: z.coerce.number().int().positive().optional(),
    lastDays: z.coerce.number().int().positive().optional(),
    dayFrom: z.coerce.number().int().positive().optional(),
    dayTo: z.coerce.number().int().positive().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.windowMode === 'first_days' && val.firstDays == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'firstDays is required',
        path: ['firstDays'],
      });
    }
    if (val.windowMode === 'last_days' && val.lastDays == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'lastDays is required',
        path: ['lastDays'],
      });
    }
    if (val.windowMode === 'day_range') {
      if (val.dayFrom == null)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'dayFrom is required',
          path: ['dayFrom'],
        });
      if (val.dayTo == null)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'dayTo is required',
          path: ['dayTo'],
        });
      if (val.dayFrom != null && val.dayTo != null && val.dayFrom > val.dayTo)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'dayFrom must be less than or equal to dayTo',
          path: ['dayTo'],
        });
    }
  });
export type CompareConfigurationsInput = z.infer<typeof compareConfigurationsSchema>;

export const compareKpiSetSchema = z.object({
  totalCalls: z.number(),
  bookingsMade: z.number(),
  bookingsAttended: z.number(),
  paymentLinksSent: z.number(),
  paymentLinksPaid: z.number(),
  financeLinksSent: z.number(),
});
export type CompareKpiSet = z.infer<typeof compareKpiSetSchema>;

/** Keyed by group id. */
export type CompareConfigurationsResult = Record<string, CompareKpiSet>;

export const kpiDataSchema = z.object({
  totalCalls: z.number(),
  answered: z.number(),
  leadsCaptured: z.number(),
  showroomInterest: z.number(),
  immediateHangups: z.number(),
});
export type KpiData = z.infer<typeof kpiDataSchema>;

export const funnelDataSchema = z.object({
  totalCalls: z.number(),
  answered: z.number().optional(),
  engaged: z.number(),
  leadCaptured: z.number(),
  vehicleChosen: z.number(),
  finance: z.number(),
  outright: z.number(),
  linkSent: z.number(),
  linkClicked: z.number(),
  showroomInterest: z.number(),
});
export type FunnelData = z.infer<typeof funnelDataSchema>;

export const recentLeadSchema = z.object({
  id: z.string(),
  executed_at: z.string(),
  customer_name: z.string().nullable(),
  vehicle_interest: z.string().nullable(),
  budget: z.string().nullable(),
  finance_preference: z.enum(['FINANCE', 'OUTRIGHT', 'UNSURE']).nullable(),
  showroom_interest: z.boolean().nullable(),
  direction: z.enum(['INBOUND', 'OUTBOUND']),
  link_clicked: z.boolean(),
  outcome: z.string(),
  buying_intent_level: z.number().nullable(),
});
export type RecentLead = z.infer<typeof recentLeadSchema>;

// --- Mind Map / Tree analytics ---

export const treeNodeMetricSchema = z.object({
  count: z.number(),
  change: z.number().nullable(),
  inbound: z.number(),
  outbound: z.number(),
});
export type TreeNodeMetric = z.infer<typeof treeNodeMetricSchema>;

export const treeDashboardDataSchema = z.object({
  totalCalls: treeNodeMetricSchema,
  answered: treeNodeMetricSchema,
  notAnswered: treeNodeMetricSchema,
  noAnswer: treeNodeMetricSchema,
  busy: treeNodeMetricSchema,
  voicemail: treeNodeMetricSchema,
  pickedUp: treeNodeMetricSchema,
  engaged: treeNodeMetricSchema,
  immediateHangups: treeNodeMetricSchema,
  salesPipeline: treeNodeMetricSchema,
  vehicleChosen: treeNodeMetricSchema,
  finance: treeNodeMetricSchema,
  outright: treeNodeMetricSchema,
  linkSent: treeNodeMetricSchema,
  linkClicked: treeNodeMetricSchema,
  showroomBooked: treeNodeMetricSchema,
  totalInquiries: treeNodeMetricSchema,
  callbackRequested: treeNodeMetricSchema,
  alreadyPurchased: treeNodeMetricSchema,
  financeInquiry: treeNodeMetricSchema,
  refundRequest: treeNodeMetricSchema,
  administrative: treeNodeMetricSchema,
  vehicleNotFound: treeNodeMetricSchema,
});
export type TreeDashboardData = z.infer<typeof treeDashboardDataSchema>;

export const dashboardDataSchema = z.object({
  kpi: kpiDataSchema,
  funnel: funnelDataSchema,
  tree: treeDashboardDataSchema,
});
export type DashboardData = z.infer<typeof dashboardDataSchema>;

// --- Configured (generic) dashboard response ---

export const qualitySummarySchema = z.object({
  avgQualityScore: z.number().nullable(),
  avgTechnicalScore: z.number().nullable(),
  avgConversationScore: z.number().nullable(),
  avgTaskCompletionScore: z.number().nullable(),
  avgPromptAdherenceScore: z.number().nullable(),
  goodCallRate: z.number().nullable(),
  totalReviewed: z.number(),
});
export type QualitySummary = z.infer<typeof qualitySummarySchema>;

export const configuredDashboardResponseSchema = z.object({
  config: dashboardConfigSchema,
  data: z.record(z.string(), treeNodeMetricSchema),
  summary: qualitySummarySchema.optional(),
});
export type ConfiguredDashboardResponse = z.infer<typeof configuredDashboardResponseSchema>;

export const leadsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  direction: directionSchema.default('ALL'),
  executedAtFrom: z.coerce.date().optional(),
  executedAtTo: z.coerce.date().optional(),
  timePreset: timePresetSchema,
  customTimeFrom: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  customTimeTo: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  showroom: z.enum(['all', 'yes', 'no']).default('all'),
  finance: z.enum(['all', 'finance', 'outright', 'unsure']).default('all'),
  linkClicked: z.enum(['all', 'yes', 'no']).default('all'),
  campaignId: z.string().uuid().optional(),
  minBuyingIntent: z.coerce.number().int().min(1).max(5).optional(),
});
export type LeadsQuery = z.infer<typeof leadsQuerySchema>;

export interface PaginatedLeadsResponse {
  data: RecentLead[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// --- Inquiry (non-sales) analytics schemas ---

export const inquiryBreakdownSchema = z.object({
  outcome: z.string(),
  count: z.number(),
  percentage: z.number(),
});
export type InquiryBreakdown = z.infer<typeof inquiryBreakdownSchema>;

export const inquiryKpiDataSchema = z.object({
  totalInquiries: z.number(),
  callbackRequested: z.number(),
  alreadyPurchased: z.number(),
  financeInquiry: z.number(),
  refundRequest: z.number(),
  administrative: z.number(),
});
export type InquiryKpiData = z.infer<typeof inquiryKpiDataSchema>;

export const recentInquirySchema = z.object({
  id: z.string(),
  executed_at: z.string(),
  customer_name: z.string().nullable(),
  outcome: z.string(),
  reason: z.string().nullable(),
  direction: z.enum(['INBOUND', 'OUTBOUND']),
});
export type RecentInquiry = z.infer<typeof recentInquirySchema>;

export const inquiriesQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  direction: directionSchema.default('ALL'),
  executedAtFrom: z.coerce.date().optional(),
  executedAtTo: z.coerce.date().optional(),
  timePreset: timePresetSchema,
  customTimeFrom: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  customTimeTo: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  campaignId: z.string().uuid().optional(),
  outcomeFilter: z
    .enum([
      'all',
      'CALLBACK_REQUESTED',
      'ALREADY_PURCHASED',
      'FINANCE_INQUIRY',
      'REFUND_REQUEST',
      'ADMINISTRATIVE',
    ])
    .default('all'),
});
export type InquiriesQuery = z.infer<typeof inquiriesQuerySchema>;

export const inquiryDashboardDataSchema = z.object({
  kpi: inquiryKpiDataSchema,
  breakdown: z.array(inquiryBreakdownSchema),
});
export type InquiryDashboardData = z.infer<typeof inquiryDashboardDataSchema>;

export interface PaginatedInquiriesResponse {
  data: RecentInquiry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const callClassificationSchema = z.enum([
  'all',
  'answered',
  'not_answered',
  'no_answer',
  'busy',
  'voicemail',
  'picked_up',
  'engaged',
  'immediate_hangup',
  'sales_pipeline',
  'vehicle_chosen',
  'finance',
  'outright',
  'link_sent',
  'link_clicked',
  'showroom_booked',
  'showroom_sms_sent',
  'reservation_link_sms_sent',
  'car_chosen',
  'no_car_chosen',
  'change_booking',
  'ai_handles_change',
  'confirm_car',
  'explain_v12_usp',
  'link_not_clicked',
  'ai_only_completion',
  'human_handover',
  'update_showroom_spreadsheet',
  'warranty_issue',
  'warranty_info_sms_sent',
  'part_exchange',
  'inquiries',
  'callback_requested',
  'already_purchased',
  'finance_inquiry',
  'refund_request',
  'administrative',
  'vehicle_not_found',
  // Newcross-specific classifications
  'nc_answered',
  'availability_captured',
  'availability_confirmed',
  'availability_potential',
  'availability_consented',
  'availability_consent_declined',
  'availability_consent_not_obtained',
  'future_availability',
  'nc_callback_requested',
  'transferred_to_human',
  'shift_confirmed',
  'shift_declined',
  'canceled',
  'failed',
  'pending',
  'picked_up_any',
  'not_available',
  'no_interaction',
  'interacted_no_outcome',
  'will_update_on_app',
  'not_sure_availability',
  'missing_availability',
  'sent_to_webhook',
  // Call quality classifications
  'good_calls',
  'calls_with_issues',
  'technical_issues',
  'agent_silence',
  'accent_deviation',
  'audio_issues',
  'conversation_issues',
  'repetition',
  'off_topic',
  'prompt_deviation',
  'hallucination',
  'task_completion_issues',
  'goal_failure',
  'early_hangup',
  'unplanned_transfer',
  // Payment link classifications
  'payment_link_created',
  'payment_link_sent',
  'payment_link_paid',
  'payment_link_failed',
  'payment_link_expired',
  // Booking classifications (rows in the bookings table)
  'has_booking',
  'has_active_booking',
  'has_cancelled_booking',
  'has_future_booking',
]);
export type CallClassification = z.infer<typeof callClassificationSchema>;

export const callsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  mode: analyticsModeSchema.optional(),
  // Coerce empty/missing → 'all' so v2 dashboards (where the node has no
  // legacy classification because the drilldown drives off metricKey) don't
  // 400 on validation. `.default()` alone only fires for `undefined`.
  classification: z.preprocess(
    (v) => (v === '' || v == null ? 'all' : v),
    callClassificationSchema,
  ),
  // v2 drilldown: when ANALYTICS_V2_ENABLED is on and `metricKey` is provided,
  // the backend resolves the metric's predicate via MetricResolver and uses
  // it as the WHERE clause — reusing the exact filter the dashboard count
  // ran. Falls back to `classification` (legacy switch) otherwise.
  metricKey: z.string().optional(),
  excludeMetricKeys: z
    .string()
    .transform((s) => s.split(',').filter(Boolean))
    .pipe(z.array(z.string()))
    .optional(),
  excludeClassifications: z
    .string()
    .transform((s) => s.split(',').filter(Boolean))
    .pipe(z.array(callClassificationSchema))
    .optional(),
  direction: directionSchema.default('ALL'),
  executedAtFrom: z.coerce.date().optional(),
  executedAtTo: z.coerce.date().optional(),
  timePreset: timePresetSchema,
  customTimeFrom: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  customTimeTo: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  campaignId: z.string().uuid().optional(),
  callTransport: z.enum(['PSTN', 'WEBRTC', 'WHATSAPP', 'WEB_WIDGET', 'EMAIL']).optional(),
  minRating: z.coerce.number().int().min(1).max(10).optional(),
});
export type CallsQuery = z.infer<typeof callsQuerySchema>;

export const callListItemSchema = z.object({
  id: z.string(),
  contact_id: z.string().nullable().optional(),
  executed_at: z.string(),
  customer_name: z.string().nullable(),
  phone_number: z.string().nullable(),
  outcome: z.string(),
  direction: z.enum(['INBOUND', 'OUTBOUND']),
  duration: z.number().nullable(),
  buying_intent_level: z.number().nullable(),
  rating_score: z.number().nullable(),
  vehicle_not_found: z.boolean().nullable(),
});
export type CallListItem = z.infer<typeof callListItemSchema>;

export interface PaginatedCallsResponse {
  data: CallListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// --- Availability analytics schemas ---

export const availabilityKpiDataSchema = z.object({
  totalCaptured: z.number(),
  answeredCalls: z.number(),
  captureRate: z.number(),
  totalSlots: z.number(),
  avgSlotsPerCall: z.number(),
  consentedCount: z.number(),
  consentRate: z.number(),
  inboundCaptured: z.number(),
  outboundCaptured: z.number(),
});
export type AvailabilityKpiData = z.infer<typeof availabilityKpiDataSchema>;

export const shiftTypeBreakdownSchema = z.object({
  shiftType: z.string(),
  count: z.number(),
});
export type ShiftTypeBreakdown = z.infer<typeof shiftTypeBreakdownSchema>;

export const dailyTrendItemSchema = z.object({
  date: z.string(),
  count: z.number(),
});
export type DailyTrendItem = z.infer<typeof dailyTrendItemSchema>;

export const campaignBreakdownItemSchema = z.object({
  campaignId: z.string().nullable(),
  campaignName: z.string(),
  count: z.number(),
});
export type CampaignBreakdownItem = z.infer<typeof campaignBreakdownItemSchema>;

// Where each half of the dashboard came from. Availability counts follow
// AVAILABILITY_SOURCE_TOOLS ('tool' = only what reached the booking system);
// consent is always 'review', because the capture tool is called before consent
// is asked and so never carries it.
export const availabilitySourcesSchema = z.object({
  availability: z.enum(['tool', 'review']),
  consent: z.literal('review'),
});
export type AvailabilitySources = z.infer<typeof availabilitySourcesSchema>;

export const availabilityDashboardDataSchema = z.object({
  sources: availabilitySourcesSchema,
  kpi: availabilityKpiDataSchema,
  shiftTypeBreakdown: z.array(shiftTypeBreakdownSchema),
  directionSplit: z.object({
    inbound: z.number(),
    outbound: z.number(),
  }),
  consentBreakdown: z.object({
    consented: z.number(),
    declined: z.number(),
    notObtained: z.number(),
  }),
  dailyTrend: z.array(dailyTrendItemSchema),
  campaignBreakdown: z.array(campaignBreakdownItemSchema),
});
export type AvailabilityDashboardData = z.infer<typeof availabilityDashboardDataSchema>;

export const recentCaptureSchema = z.object({
  id: z.string(),
  executed_at: z.string(),
  phone_number: z.string().nullable(),
  contact_name: z.string().nullable(),
  slots_count: z.number(),
  shift_types: z.array(z.string()),
  confirmed: z.boolean(),
  consented: z.boolean(),
  direction: z.enum(['INBOUND', 'OUTBOUND']),
  earliest_slot_date: z.string().nullable(),
  latest_slot_date: z.string().nullable(),
});
export type RecentCapture = z.infer<typeof recentCaptureSchema>;

export const availabilityRecentQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  direction: directionSchema.default('ALL'),
  executedAtFrom: z.coerce.date().optional(),
  executedAtTo: z.coerce.date().optional(),
  timePreset: timePresetSchema,
  customTimeFrom: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  customTimeTo: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  campaignId: z.string().uuid().optional(),
});
export type AvailabilityRecentQuery = z.infer<typeof availabilityRecentQuerySchema>;

export interface PaginatedCapturesResponse {
  data: RecentCapture[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}
